import { useCallback, useEffect, useRef, useState } from "react";
import {
  addAdmin,
  deleteBackup,
  deleteCourse,
  deleteQuestion,
  getCourseQuestionCount,
  getCourseQuestions,
  getCourses,
  importQuestions,
  listAdmins,
  listBackups,
  listEnrollments,
  restoreBackup,
  saveEnrollment,
  saveQuestion,
  setCourseRegistrationDate,
  upsertCourse,
} from "../lib/db";
import {
  buildTemplateWorkbook,
  parseQuestionsWorkbook,
  type ParsedRow,
} from "../lib/xlsx";
import { uploadQuizImage } from "../lib/storage";
import { accessExpiry, isAccessExpired } from "../lib/access";
import type {
  Backup,
  Choice,
  Course,
  Enrollment,
  Question,
} from "../lib/types";
import "./Admin.css";

type Tab = "courses" | "questions" | "import" | "access" | "admins" | "restore";

export default function Admin() {
  const [tab, setTab] = useState<Tab>("courses");
  return (
    <div className="admin">
      <header className="admin__header">
        <h1>Administración</h1>
        <nav className="admin__tabs">
          {(
            [
              ["courses", "Cursos"],
              ["questions", "Preguntas"],
              ["import", "Importar preguntas"],
              ["access", "Accesos"],
              ["admins", "Administradores"],
              ["restore", "Restaurar"],
            ] as [Tab, string][]
          ).map(([key, label]) => (
            <button
              key={key}
              className={"admin__tab" + (tab === key ? " admin__tab--active" : "")}
              onClick={() => setTab(key)}
            >
              {label}
            </button>
          ))}
        </nav>
      </header>

      {tab === "courses" && <CoursesPanel />}
      {tab === "questions" && <QuestionsPanel />}
      {tab === "import" && <ImportPanel />}
      {tab === "access" && <AccessPanel />}
      {tab === "admins" && <AdminsPanel />}
      {tab === "restore" && <RestorePanel />}
    </div>
  );
}

// --- Confirmation dialog ---------------------------------------------------
// A promise-based confirm so any destructive action can `await confirm(...)`.

interface ConfirmOptions {
  title: string;
  message: string;
  confirmLabel?: string;
  danger?: boolean;
}

function useConfirm() {
  const [opts, setOpts] = useState<ConfirmOptions | null>(null);
  const resolver = useRef<((ok: boolean) => void) | null>(null);

  const confirm = useCallback((o: ConfirmOptions) => {
    setOpts(o);
    return new Promise<boolean>((resolve) => {
      resolver.current = resolve;
    });
  }, []);

  const settle = (ok: boolean) => {
    resolver.current?.(ok);
    resolver.current = null;
    setOpts(null);
  };

  const dialog = opts ? (
    <div className="modal__backdrop" onClick={() => settle(false)}>
      <div
        className="modal card"
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <h3>{opts.title}</h3>
        <p className="modal__message">{opts.message}</p>
        <div className="editor__actions">
          <button className="btn btn--muted" onClick={() => settle(false)}>
            Cancelar
          </button>
          <button
            className={"btn" + (opts.danger ? " btn--danger" : "")}
            onClick={() => settle(true)}
          >
            {opts.confirmLabel || "Confirmar"}
          </button>
        </div>
      </div>
    </div>
  ) : null;

  return { confirm, dialog };
}

// --- Courses ---------------------------------------------------------------

function CoursesPanel() {
  const [courses, setCourses] = useState<Course[]>([]);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [editing, setEditing] = useState<Course | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const { confirm, dialog } = useConfirm();

  async function refresh() {
    const cs = await getCourses();
    setCourses(cs);
    const entries = await Promise.all(
      cs.map(async (c) => [c.id, await getCourseQuestionCount(c.id)] as const),
    );
    setCounts(Object.fromEntries(entries));
  }
  useEffect(() => {
    refresh().catch(console.error);
  }, []);

  function blank(): Course {
    return {
      id: "",
      title: "",
      description: "",
      order: courses.length + 1,
      questionsPerAttempt: 15,
      active: true,
    };
  }

  async function save(c: Course) {
    if (!c.id || !c.title) {
      setMsg("El id y el título son obligatorios.");
      return;
    }
    await upsertCourse(c);
    setEditing(null);
    setMsg("Curso guardado.");
    await refresh();
  }

  async function remove(c: Course) {
    const n = counts[c.id] ?? 0;
    const ok = await confirm({
      title: "Eliminar curso",
      message:
        `Se eliminará el curso «${c.title}» y sus ${n} pregunta(s). ` +
        `Se guardará una copia de seguridad para poder restaurarlo desde la pestaña «Restaurar».`,
      confirmLabel: "Eliminar curso",
      danger: true,
    });
    if (!ok) return;
    await deleteCourse(c);
    setMsg(`Curso «${c.title}» eliminado. Copia de seguridad guardada.`);
    await refresh();
  }

  return (
    <section className="panel">
      {dialog}
      {msg && <p className="panel__msg">{msg}</p>}
      <div className="panel__actions">
        <button className="btn" onClick={() => setEditing(blank())}>
          + Nuevo curso
        </button>
      </div>

      <table className="admin-table">
        <thead>
          <tr>
            <th>ID</th>
            <th>Título</th>
            <th>Preguntas</th>
            <th>Por intento</th>
            <th>Activo</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {courses.map((c) => (
            <tr key={c.id}>
              <td><code>{c.id}</code></td>
              <td>{c.title}</td>
              <td>{counts[c.id] ?? "…"}</td>
              <td>{c.questionsPerAttempt}</td>
              <td>{c.active ? "Sí" : "No"}</td>
              <td className="admin-table__rowactions">
                <button className="btn btn--ghost" onClick={() => setEditing(c)}>
                  Editar
                </button>
                <button
                  className="btn btn--ghost btn--ghost-danger"
                  onClick={() => remove(c)}
                >
                  Eliminar
                </button>
              </td>
            </tr>
          ))}
          {courses.length === 0 && (
            <tr><td colSpan={6} className="admin-table__empty">Sin cursos todavía.</td></tr>
          )}
        </tbody>
      </table>

      {editing && (
        <CourseEditor
          course={editing}
          onCancel={() => setEditing(null)}
          onSave={save}
        />
      )}
    </section>
  );
}

function CourseEditor({
  course,
  onCancel,
  onSave,
}: {
  course: Course;
  onCancel: () => void;
  onSave: (c: Course) => void;
}) {
  const [c, setC] = useState<Course>(course);
  const set = (patch: Partial<Course>) => setC((p) => ({ ...p, ...patch }));
  return (
    <div className="editor card">
      <h3>{course.id ? "Editar curso" : "Nuevo curso"}</h3>
      <label>
        ID (sin espacios, ej. galope1)
        <input
          value={c.id}
          disabled={!!course.id}
          onChange={(e) => set({ id: e.target.value.trim() })}
        />
      </label>
      <label>
        Título
        <input value={c.title} onChange={(e) => set({ title: e.target.value })} />
      </label>
      <label>
        Descripción
        <textarea
          value={c.description}
          onChange={(e) => set({ description: e.target.value })}
        />
      </label>
      <div className="editor__row">
        <label>
          Orden
          <input
            type="number"
            value={c.order}
            onChange={(e) => set({ order: Number(e.target.value) })}
          />
        </label>
        <label>
          Preguntas por intento
          <input
            type="number"
            value={c.questionsPerAttempt}
            onChange={(e) => set({ questionsPerAttempt: Number(e.target.value) })}
          />
        </label>
        <label>
          Validez (días)
          <input
            type="number"
            min={0}
            value={c.accessDurationDays ?? 0}
            onChange={(e) =>
              set({ accessDurationDays: Math.max(0, Number(e.target.value)) })
            }
          />
        </label>
        <label className="editor__check">
          <input
            type="checkbox"
            checked={c.active}
            onChange={(e) => set({ active: e.target.checked })}
          />
          Activo
        </label>
      </div>
      <p className="editor__hint">
        Validez = días que dura el acceso desde la fecha de registro de cada
        usuario. <strong>0 = sin caducidad</strong> (acceso permanente).
      </p>
      <div className="editor__actions">
        <button className="btn btn--muted" onClick={onCancel}>Cancelar</button>
        <button className="btn" onClick={() => onSave(c)}>Guardar</button>
      </div>
    </div>
  );
}

// --- Questions -------------------------------------------------------------

const MAX_PREVIEW = 60;

function correctSummary(q: Question): string {
  if (q.type === "choice")
    return q.choices?.find((c) => c.correct)?.text || "(imagen)";
  return q.acceptedAnswers?.join(" / ") || "—";
}

function QuestionsPanel() {
  const [courses, setCourses] = useState<Course[]>([]);
  const [courseId, setCourseId] = useState("");
  const [questions, setQuestions] = useState<Question[]>([]);
  const [editing, setEditing] = useState<Question | null>(null);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const { confirm, dialog } = useConfirm();

  useEffect(() => {
    getCourses()
      .then((cs) => {
        setCourses(cs);
        if (cs[0]) setCourseId(cs[0].id);
      })
      .catch(console.error);
  }, []);

  const refresh = useCallback(async () => {
    if (!courseId) return;
    setLoading(true);
    try {
      setQuestions(await getCourseQuestions(courseId));
    } finally {
      setLoading(false);
    }
  }, [courseId]);

  useEffect(() => {
    refresh().catch(console.error);
  }, [refresh]);

  function blank(): Question {
    return {
      id: `${courseId}__${Date.now()}`,
      courseId,
      type: "choice",
      text: "",
      choices: [
        { id: "a", text: "", correct: true },
        { id: "b", text: "", correct: false },
      ],
      acceptedAnswers: [],
      active: true,
    };
  }

  async function onSave(next: Question, previous?: Question) {
    await saveQuestion(next, previous);
    setEditing(null);
    setMsg(previous ? "Pregunta actualizada." : "Pregunta creada.");
    await refresh();
  }

  async function remove(q: Question) {
    const ok = await confirm({
      title: "Eliminar pregunta",
      message:
        `Se eliminará la pregunta «${(q.text || q.id).slice(0, MAX_PREVIEW)}». ` +
        `Se guardará una copia de seguridad para poder restaurarla.`,
      confirmLabel: "Eliminar",
      danger: true,
    });
    if (!ok) return;
    await deleteQuestion(q);
    setMsg("Pregunta eliminada. Copia de seguridad guardada.");
    await refresh();
  }

  return (
    <section className="panel">
      {dialog}
      {msg && <p className="panel__msg">{msg}</p>}

      <div className="panel__actions">
        <label className="editor__check" style={{ fontWeight: 700 }}>
          Curso:
          <select
            value={courseId}
            onChange={(e) => setCourseId(e.target.value)}
            style={{ marginLeft: 8, padding: "0.4rem 0.6rem", borderRadius: 8 }}
          >
            {courses.map((c) => (
              <option key={c.id} value={c.id}>
                {c.title} ({c.id})
              </option>
            ))}
          </select>
        </label>
        <button
          className="btn"
          disabled={!courseId}
          onClick={() => setEditing(blank())}
        >
          + Nueva pregunta
        </button>
      </div>

      <table className="admin-table">
        <thead>
          <tr>
            <th>Pregunta</th>
            <th>Tipo</th>
            <th>Correcta</th>
            <th>Activa</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {questions.map((q) => (
            <tr key={q.id}>
              <td>
                {q.imageUrl && <span title="Tiene imagen">📷 </span>}
                {q.text || <em>(sin texto)</em>}
              </td>
              <td>{q.type}</td>
              <td>{correctSummary(q)}</td>
              <td>{q.active ? "Sí" : "No"}</td>
              <td className="admin-table__rowactions">
                <button className="btn btn--ghost" onClick={() => setEditing(q)}>
                  Editar
                </button>
                <button
                  className="btn btn--ghost btn--ghost-danger"
                  onClick={() => remove(q)}
                >
                  Eliminar
                </button>
              </td>
            </tr>
          ))}
          {questions.length === 0 && (
            <tr>
              <td colSpan={5} className="admin-table__empty">
                {loading ? "Cargando…" : "Sin preguntas en este curso."}
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {editing && (
        <QuestionEditor
          question={editing}
          onCancel={() => setEditing(null)}
          onSave={onSave}
        />
      )}
    </section>
  );
}

function QuestionEditor({
  question,
  onCancel,
  onSave,
}: {
  question: Question;
  onCancel: () => void;
  onSave: (next: Question, previous?: Question) => void;
}) {
  // Whether we're editing an existing doc (has a backup-worthy prior version).
  const isExisting = !!question.updatedAt || !!question.createdAt;
  const [q, setQ] = useState<Question>(question);
  const [answers, setAnswers] = useState(
    (question.acceptedAnswers || []).join("\n"),
  );
  const [error, setError] = useState<string | null>(null);
  const set = (patch: Partial<Question>) => setQ((p) => ({ ...p, ...patch }));

  const choices = q.choices || [];
  const setChoice = (idx: number, patch: Partial<Choice>) =>
    set({ choices: choices.map((c, i) => (i === idx ? { ...c, ...patch } : c)) });
  const setCorrect = (idx: number) =>
    set({ choices: choices.map((c, i) => ({ ...c, correct: i === idx })) });
  const addChoice = () =>
    set({
      choices: [
        ...choices,
        { id: String.fromCharCode(97 + choices.length), text: "", correct: false },
      ],
    });
  const removeChoice = (idx: number) => {
    const next = choices.filter((_, i) => i !== idx);
    if (next.length && !next.some((c) => c.correct)) next[0].correct = true;
    set({ choices: next });
  };

  function submit() {
    if (!q.text.trim() && !q.imageUrl?.trim()) {
      setError("La pregunta necesita texto o una imagen.");
      return;
    }
    const next: Question = { ...q, text: q.text.trim() };
    if (q.type === "choice") {
      const cleaned = choices
        .map((c) => ({ ...c, text: c.text.trim() }))
        .filter((c) => c.text || c.imageUrl);
      if (cleaned.length < 2) {
        setError("Las preguntas de opción necesitan al menos 2 opciones.");
        return;
      }
      if (!cleaned.some((c) => c.correct)) {
        setError("Marca cuál es la opción correcta.");
        return;
      }
      next.choices = cleaned;
      next.acceptedAnswers = [];
    } else {
      const accepted = answers
        .split("\n")
        .map((s) => s.trim())
        .filter(Boolean);
      if (accepted.length === 0) {
        setError("Añade al menos una respuesta aceptada.");
        return;
      }
      next.acceptedAnswers = accepted;
      next.choices = [];
    }
    onSave(next, isExisting ? question : undefined);
  }

  return (
    <div className="modal__backdrop">
      <div className="editor card editor--wide editor--modal">
      <h3>{isExisting ? "Editar pregunta" : "Nueva pregunta"}</h3>
      {error && <p className="editor__error">{error}</p>}

      <div className="editor__row">
        <label>
          Tipo
          <select
            value={q.type}
            onChange={(e) =>
              set({ type: e.target.value as Question["type"] })
            }
          >
            <option value="choice">Opción múltiple</option>
            <option value="text">Texto libre</option>
          </select>
        </label>
        <label className="editor__check">
          <input
            type="checkbox"
            checked={q.active}
            onChange={(e) => set({ active: e.target.checked })}
          />
          Activa
        </label>
      </div>

      <label>
        Enunciado
        <textarea
          value={q.text}
          onChange={(e) => set({ text: e.target.value })}
        />
      </label>

      <label>
        Imagen de la pregunta (URL, opcional)
        <input
          value={q.imageUrl || ""}
          placeholder="https://…"
          onChange={(e) => set({ imageUrl: e.target.value || undefined })}
        />
      </label>

      {q.type === "choice" ? (
        <div className="choices-editor">
          <span className="choices-editor__label">
            Opciones (marca la correcta)
          </span>
          {choices.map((c, i) => (
            <div key={i} className="choice-row">
              <input
                type="radio"
                name="correct"
                checked={!!c.correct}
                onChange={() => setCorrect(i)}
                title="Marcar como correcta"
              />
              <input
                className="choice-row__text"
                value={c.text}
                placeholder={`Opción ${i + 1}`}
                onChange={(e) => setChoice(i, { text: e.target.value })}
              />
              <input
                className="choice-row__img"
                value={c.imageUrl || ""}
                placeholder="Imagen URL (opcional)"
                onChange={(e) =>
                  setChoice(i, { imageUrl: e.target.value || undefined })
                }
              />
              <button
                type="button"
                className="choice-row__remove"
                disabled={choices.length <= 2}
                onClick={() => removeChoice(i)}
                title="Quitar opción"
              >
                ✕
              </button>
            </div>
          ))}
          <button type="button" className="btn btn--ghost" onClick={addChoice}>
            + Añadir opción
          </button>
        </div>
      ) : (
        <label>
          Respuestas aceptadas (una por línea)
          <textarea
            value={answers}
            placeholder={"caballo\ncaballos"}
            onChange={(e) => setAnswers(e.target.value)}
          />
        </label>
      )}

      <div className="editor__actions">
        <button className="btn btn--muted" onClick={onCancel}>
          Cancelar
        </button>
        <button className="btn" onClick={submit}>
          Guardar
        </button>
      </div>
      </div>
    </div>
  );
}

// --- Import ----------------------------------------------------------------

function ImportPanel() {
  const [courses, setCourses] = useState<Course[]>([]);
  const [targetCourse, setTargetCourse] = useState("");
  const [rows, setRows] = useState<ParsedRow[] | null>(null);
  const [sheetName, setSheetName] = useState("");
  const [fileName, setFileName] = useState("");
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    getCourses()
      .then((cs) => {
        setCourses(cs);
        if (cs[0]) setTargetCourse(cs[0].id);
      })
      .catch(console.error);
  }, []);

  async function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    setMsg(null);
    try {
      const result = await parseQuestionsWorkbook(await file.arrayBuffer());
      setSheetName(result.sheetName);
      setRows(result.rows);
      if (result.rows.length === 0)
        setMsg("No se encontraron preguntas. ¿Tiene la hoja una fila de encabezados?");
    } catch (err) {
      console.error(err);
      setMsg("No se pudo leer el archivo. ¿Es un .xlsx válido?");
    }
  }

  const courseOf = (r: ParsedRow) => r.question.courseId || targetCourse;
  const errorOf = (r: ParsedRow): string | null => {
    if (r.question.error) return r.question.error;
    if (!courseOf(r)) return "Falta el curso (elige un curso destino)";
    return null;
  };

  const valid = rows?.filter((r) => !errorOf(r)) ?? [];
  const invalid = (rows ?? [])
    .map((r) => ({ r, err: errorOf(r) }))
    .filter((x) => x.err);
  const imgCount = (r: ParsedRow) =>
    (r.pendingQuestionImage ? 1 : 0) +
    r.pendingChoiceImages.filter(Boolean).length;

  async function doImport() {
    if (valid.length === 0) return;
    setImporting(true);
    setProgress(0);
    setMsg(null);
    try {
      const prepared: Question[] = [];
      for (let i = 0; i < valid.length; i++) {
        const r = valid[i];
        const q = { ...r.question } as Question;
        q.courseId = courseOf(r);
        q.id = (q.id || "").replace(/^curso__/, `${q.courseId}__`);
        if (r.pendingQuestionImage) {
          q.imageUrl = await uploadQuizImage(
            r.pendingQuestionImage.blob,
            "questions",
            r.pendingQuestionImage.filename,
          );
        }
        if (q.choices) {
          q.choices = await Promise.all(
            q.choices.map(async (c, idx) => {
              const pend = r.pendingChoiceImages[idx];
              return pend
                ? { ...c, imageUrl: await uploadQuizImage(pend.blob, "choices", pend.filename) }
                : c;
            }),
          );
        }
        prepared.push(q);
        setProgress(i + 1);
      }
      await importQuestions(prepared);
      setMsg(`Importadas ${prepared.length} preguntas correctamente.`);
      setRows(null);
      setFileName("");
    } catch (err) {
      console.error(err);
      setMsg("Error al importar. Revisa la consola.");
    } finally {
      setImporting(false);
    }
  }

  async function downloadTemplate() {
    const ids = courses.map((c) => c.id);
    const blob = await buildTemplateWorkbook(ids.length ? ids : undefined);
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "plantilla-preguntas-equiforall.xlsx";
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <section className="panel">
      {msg && <p className="panel__msg">{msg}</p>}
      <div className="import__intro card">
        <p>
          Sube un <strong>.xlsx</strong> con una pregunta por fila (hoja{" "}
          <code>Preguntas</code>). Las imágenes pueden ir <strong>arrastradas en
          la celda</strong> o como enlace (URL). Marca la opción correcta con su
          número en <code>Correcta</code> (1–4).
        </p>
        <button className="btn btn--ghost" onClick={downloadTemplate}>
          ↓ Descargar plantilla
        </button>
      </div>

      <div className="panel__actions">
        <label className="editor__check" style={{ fontWeight: 700 }}>
          Curso destino (si la fila no indica curso):
          <select
            value={targetCourse}
            onChange={(e) => setTargetCourse(e.target.value)}
            style={{ marginLeft: 8, padding: "0.4rem 0.6rem", borderRadius: 8 }}
          >
            <option value="">— elegir —</option>
            {courses.map((c) => (
              <option key={c.id} value={c.id}>{c.title} ({c.id})</option>
            ))}
          </select>
        </label>
      </div>

      <div className="panel__actions">
        <label className="btn">
          {fileName || "Seleccionar archivo .xlsx"}
          <input type="file" accept=".xlsx,.xls" hidden onChange={onFile} />
        </label>
        {valid.length > 0 && (
          <button className="btn" disabled={importing} onClick={doImport}>
            {importing
              ? `Importando… ${progress}/${valid.length}`
              : `Importar ${valid.length} preguntas`}
          </button>
        )}
      </div>

      {rows && (
        <>
          <p className="import__summary">
            Hoja <strong>{sheetName}</strong> · {valid.length} válidas ·{" "}
            {invalid.length} con errores
          </p>
          {invalid.length > 0 && (
            <table className="admin-table admin-table--errors">
              <thead><tr><th>Fila</th><th>Pregunta</th><th>Error</th></tr></thead>
              <tbody>
                {invalid.map(({ r, err }) => (
                  <tr key={r.rowNumber}>
                    <td>{r.rowNumber}</td>
                    <td>{r.question.text || <em>(vacía)</em>}</td>
                    <td className="err">{err}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <table className="admin-table">
            <thead>
              <tr><th>Fila</th><th>Curso</th><th>Tipo</th><th>Pregunta</th><th>Correcta</th><th>Img</th></tr>
            </thead>
            <tbody>
              {valid.slice(0, 100).map((r) => {
                const q = r.question as Question;
                const correct =
                  q.type === "choice"
                    ? q.choices?.find((c) => c.correct)?.text || "(imagen)"
                    : q.acceptedAnswers?.join(" / ");
                const n = imgCount(r);
                return (
                  <tr key={r.rowNumber}>
                    <td>{r.rowNumber}</td>
                    <td><code>{courseOf(r)}</code></td>
                    <td>{q.type}</td>
                    <td>{q.text}</td>
                    <td>{correct}</td>
                    <td>{n ? `📷 ${n}` : ""}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {valid.length > 100 && (
            <p className="import__summary">… y {valid.length - 100} más.</p>
          )}
        </>
      )}
    </section>
  );
}

// --- Access (enrollments) --------------------------------------------------

function msToDateInput(ms: number | undefined): string {
  if (!ms) return "";
  const d = new Date(ms);
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}
function formatDay(ms: number): string {
  return msToDateInput(ms);
}
function IsoDatePicker({
  value,
  onChange,
}: {
  value: number | undefined;
  onChange: (v: string) => void;
}) {
  const iso = msToDateInput(value);
  return (
    <span
      style={{
        position: "relative",
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        cursor: "pointer",
      }}
    >
      <span>{iso || "—"}</span>
      <span aria-hidden style={{ opacity: 0.6 }}>
        📅
      </span>
      <input
        type="date"
        value={iso}
        onChange={(ev) => onChange(ev.target.value)}
        aria-label="Editar fecha de registro"
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          opacity: 0,
          cursor: "pointer",
        }}
      />
    </span>
  );
}
function dateInputToMs(v: string): number | undefined {
  if (!v) return undefined;
  const ms = new Date(`${v}T00:00:00`).getTime();
  return Number.isNaN(ms) ? undefined : ms;
}

function AccessPanel() {
  const [courses, setCourses] = useState<Course[]>([]);
  const [enrollments, setEnrollments] = useState<Enrollment[]>([]);
  const [email, setEmail] = useState("");
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [msg, setMsg] = useState<string | null>(null);

  async function refresh() {
    const [cs, es] = await Promise.all([getCourses(), listEnrollments()]);
    setCourses(cs);
    setEnrollments(es);
  }
  useEffect(() => {
    refresh().catch(console.error);
  }, []);

  const courseById = (id: string) => courses.find((c) => c.id === id);

  async function grant() {
    if (!email.trim()) return;
    const lower = email.toLowerCase();
    const existing = enrollments.find((e) => e.email === lower);
    const courseMap: Record<string, boolean> = { ...(existing?.courses ?? {}) };
    const dates: Record<string, number> = { ...(existing?.enrolledAt ?? {}) };
    let any = false;
    for (const c of courses)
      if (selected[c.id]) {
        courseMap[c.id] = true;
        if (!dates[c.id]) dates[c.id] = Date.now();
        any = true;
      }
    if (!any) {
      setMsg("Selecciona al menos un curso.");
      return;
    }
    await saveEnrollment(lower, courseMap, dates);
    setMsg(`Acceso actualizado para ${lower}.`);
    setEmail("");
    setSelected({});
    await refresh();
  }

  async function editDate(e: Enrollment, courseId: string, value: string) {
    const ms = dateInputToMs(value);
    if (!ms) return;
    await setCourseRegistrationDate(e.email, courseId, ms);
    await refresh();
  }

  async function revoke(e: Enrollment, courseId: string) {
    const courseMap = { ...(e.courses ?? {}) };
    const dates = { ...(e.enrolledAt ?? {}) };
    delete courseMap[courseId];
    delete dates[courseId];
    await saveEnrollment(e.email, courseMap, dates);
    setMsg(`Acceso a ${courseId} retirado de ${e.email}.`);
    await refresh();
  }

  // Flatten to one row per (user, course-with-access).
  const rows = enrollments.flatMap((e) =>
    Object.entries(e.courses || {})
      .filter(([, v]) => v)
      .map(([courseId]) => ({ e, courseId })),
  );

  return (
    <section className="panel">
      {msg && <p className="panel__msg">{msg}</p>}
      <div className="editor card">
        <h3>Conceder / actualizar acceso</h3>
        <label>
          Correo del usuario
          <input
            type="email"
            value={email}
            placeholder="usuario@correo.com"
            onChange={(e) => setEmail(e.target.value)}
          />
        </label>
        <div className="access__courses">
          {courses.map((c) => (
            <label key={c.id} className="editor__check">
              <input
                type="checkbox"
                checked={!!selected[c.id]}
                onChange={(e) =>
                  setSelected((p) => ({ ...p, [c.id]: e.target.checked }))
                }
              />
              {c.title}
            </label>
          ))}
        </div>
        <div className="editor__actions">
          <button className="btn" onClick={grant}>Guardar acceso</button>
        </div>
      </div>

      <h3 className="panel__subtitle">Usuarios con acceso</h3>
      <table className="admin-table">
        <thead>
          <tr>
            <th>Correo</th>
            <th>Curso</th>
            <th>Fecha de registro</th>
            <th>Caduca</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {rows.map(({ e, courseId }) => {
            const course = courseById(courseId);
            const regMs = e.enrolledAt?.[courseId];
            const expiry = accessExpiry(regMs, course?.accessDurationDays);
            const expired = course
              ? isAccessExpired(e, { id: courseId, accessDurationDays: course.accessDurationDays })
              : false;
            return (
              <tr key={`${e.email}__${courseId}`}>
                <td>{e.email}</td>
                <td>{course?.title || courseId}</td>
                <td>
                  <IsoDatePicker
                    value={regMs}
                    onChange={(v) => editDate(e, courseId, v)}
                  />
                </td>
                <td>
                  {expiry === null ? (
                    <span style={{ color: "var(--text-muted)" }}>Sin caducidad</span>
                  ) : expired ? (
                    <span style={{ color: "var(--danger)", fontWeight: 600 }}>
                      Caducado · {formatDay(expiry)}
                    </span>
                  ) : (
                    formatDay(expiry)
                  )}
                </td>
                <td>
                  <button
                    className="btn btn--ghost btn--ghost-danger"
                    onClick={() => revoke(e, courseId)}
                  >
                    Retirar
                  </button>
                </td>
              </tr>
            );
          })}
          {rows.length === 0 && (
            <tr><td colSpan={5} className="admin-table__empty">Sin usuarios todavía.</td></tr>
          )}
        </tbody>
      </table>
    </section>
  );
}

// --- Admins ----------------------------------------------------------------

function AdminsPanel() {
  const [admins, setAdmins] = useState<string[]>([]);
  const [email, setEmail] = useState("");
  const [msg, setMsg] = useState<string | null>(null);

  async function refresh() {
    setAdmins(await listAdmins());
  }
  useEffect(() => {
    refresh().catch(console.error);
  }, []);

  async function add() {
    if (!email.trim()) return;
    await addAdmin(email);
    setMsg(`${email.toLowerCase()} ahora es administrador.`);
    setEmail("");
    await refresh();
  }

  return (
    <section className="panel">
      {msg && <p className="panel__msg">{msg}</p>}
      <div className="editor card">
        <h3>Añadir administrador</h3>
        <label>
          Correo
          <input
            type="email"
            value={email}
            placeholder="admin@correo.com"
            onChange={(e) => setEmail(e.target.value)}
          />
        </label>
        <div className="editor__actions">
          <button className="btn" onClick={add}>Añadir</button>
        </div>
      </div>
      <table className="admin-table">
        <thead><tr><th>Administradores</th></tr></thead>
        <tbody>
          {admins.map((a) => (
            <tr key={a}><td>{a}</td></tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

// --- Restore ---------------------------------------------------------------

function RestorePanel() {
  const [backups, setBackups] = useState<Backup[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const { confirm, dialog } = useConfirm();

  async function refresh() {
    setLoading(true);
    try {
      setBackups(await listBackups());
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    refresh().catch(console.error);
  }, []);

  function fmtDate(ms: number): string {
    try {
      return new Date(ms).toLocaleString("es-ES");
    } catch {
      return String(ms);
    }
  }

  async function restore(b: Backup) {
    const ok = await confirm({
      title: "Restaurar copia de seguridad",
      message:
        `Se volverán a crear ${b.entries.length} documento(s): «${b.label}». ` +
        `Si ya existen documentos con el mismo id, se sobrescribirán.`,
      confirmLabel: "Restaurar",
    });
    if (!ok) return;
    setBusy(b.id);
    try {
      await restoreBackup(b);
      setMsg("Copia de seguridad restaurada.");
      await refresh();
    } finally {
      setBusy(null);
    }
  }

  async function discard(b: Backup) {
    const ok = await confirm({
      title: "Eliminar copia de seguridad",
      message:
        `Se eliminará permanentemente la copia «${b.label}». ` +
        `Esta acción no se puede deshacer.`,
      confirmLabel: "Eliminar copia",
      danger: true,
    });
    if (!ok) return;
    setBusy(b.id);
    try {
      await deleteBackup(b.id);
      setMsg("Copia de seguridad eliminada.");
      await refresh();
    } finally {
      setBusy(null);
    }
  }

  return (
    <section className="panel">
      {dialog}
      {msg && <p className="panel__msg">{msg}</p>}
      <p className="import__summary">
        Cada acción destructiva (eliminar un curso o una pregunta, o editar una
        pregunta) guarda aquí una copia de seguridad.
      </p>

      <table className="admin-table">
        <thead>
          <tr>
            <th>Fecha</th>
            <th>Acción</th>
            <th>Detalle</th>
            <th>Docs</th>
            <th>Por</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {backups.map((b) => (
            <tr key={b.id}>
              <td>{fmtDate(b.createdAt)}</td>
              <td>
                {b.action === "delete" ? "Eliminación" : "Edición"} ·{" "}
                {b.kind === "course" ? "curso" : "pregunta"}
                {b.restoredAt && (
                  <span className="restore__tag" title={fmtDate(b.restoredAt)}>
                    restaurada
                  </span>
                )}
              </td>
              <td>{b.label}</td>
              <td>{b.entries.length}</td>
              <td>{b.createdBy}</td>
              <td className="admin-table__rowactions">
                <button
                  className="btn btn--ghost"
                  disabled={busy === b.id}
                  onClick={() => restore(b)}
                >
                  Restaurar
                </button>
                <button
                  className="btn btn--ghost btn--ghost-danger"
                  disabled={busy === b.id}
                  onClick={() => discard(b)}
                >
                  Eliminar
                </button>
              </td>
            </tr>
          ))}
          {backups.length === 0 && (
            <tr>
              <td colSpan={6} className="admin-table__empty">
                {loading ? "Cargando…" : "Sin copias de seguridad todavía."}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </section>
  );
}
